import { normalizeService, toAccount, toConversation, toPoolAccount } from '../types';
import { readApiToken } from './api-key';
import { classifyApiFailure } from './error-copy';
import { markAdminAuthRequired, notifyAdminUnauthorized, notifyApiTokenRequired, notifySessionExpired, readAdminToken, } from './client';
/** 房間列表的寬鬆 JSON → RoomInfo；缺欄位就補推得出來的值，不因一列壞掉整份空白。 */
function toRoomInfo(raw, fallbackService) {
    const row = (raw ?? {});
    const id = String(row.id ?? row.name ?? row.slug ?? '');
    const onlineRaw = row.online ?? row.count ?? row.users;
    return {
        id,
        name: String(row.name ?? id),
        online: typeof onlineRaw === 'number' && Number.isFinite(onlineRaw) ? onlineRaw : 0,
        // 站別照後端回的走（`wootalk`／`sg` 以前會被改寫成 fallback＝看起來像 ek21）。
        service: normalizeService(row.service, fallbackService),
    };
}
const KIND_MAP = {
    say: 'say',
    pm: 'pm',
    system: 'system',
    join: 'join',
    leave: 'leave',
};
export function createHttpApiClient(options) {
    const base = options.baseUrl.replace(/\/+$/, '');
    const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    // 註解寫「預設 true」就必須真的給預設值：沒傳時 undefined 會讓 `!realtime` 恆為 true，永遠不開 WS。
    const realtimeEnabled = options.realtime ?? true;
    const reconcileMs = options.reconcileMs ?? 5000;
    /** 認證標頭：每次呼叫都重讀（登入／登出後不必重建 client）。 */
    const authHeaders = options.sessionHeaders ?? (() => ({}));
    // 走 http 就代表 admin 端點要 token：AdminShell 靠這個旗標決定要不要顯示輸入列
    // （mock 不呼叫，所以 demo 站不會冒出一個沒用的 token 欄位）。
    markAdminAuthRequired();
    /**
     * 唯一的一般請求出口。**所有請求都帶 `authHeaders()` 的 session 憑證**
     * （PIN 登入簽發的 `Authorization: Bearer …` 或 `x-vm-session`）：
     * `/api/*` 一律要 session（DECISIONS 0049）。
     *
     * 另外，寫入類請求（非 GET／HEAD）**若** sessionStorage 裡有 `x-api-token` 就順便帶上：
     * 2026-10-27 起 Worker 不再要求它（`requireServiceToken` 已不在寫入路徑上），
     * 帶了只是為了相容還開著舊閘門的部署。沒有也照樣送出——有 session 就能寫。
     * 金鑰由使用者貼在「操作金鑰」設定裡（`api/api-key.ts` 的 `sessionStorage` key），
     * 那個框現在只在後端真的回「缺金鑰」時才出現。
     *
     * 沒有 session 時**照樣送出**（讓後端回 401 並由這裡翻成看得懂的一句），
     * 不要在瀏覽器端自己假裝成功或擋下來——錯誤訊息要跟真實後端一致。
     */
    async function request(path, init = {}) {
        const method = (init.method ?? 'GET').toUpperCase();
        // 只有寫入類才帶：讀取端點後端不看這把鑰匙，多帶一個自訂標頭只會多一個 CORS 預檢欄位。
        const apiToken = method === 'GET' || method === 'HEAD' ? '' : readApiToken();
        const res = await doFetch(`${base}${path}`, {
            ...init,
            headers: {
                'content-type': 'application/json',
                ...authHeaders(),
                ...(apiToken === '' ? {} : { 'x-api-token': apiToken }),
                ...(init.headers ?? {}),
            },
        });
        const text = await res.text();
        const body = text ? safeJson(text) : undefined;
        if (!res.ok) {
            const errBody = (body ?? {});
            // **判斷一律看 body**（code／error／detail），不是只看 status：401 有兩種完全不同的病，
            // 一種是 session 過期，一種是缺 `x-api-token`（見 `api/error-copy.ts` 的檔頭）。
            const failure = classifyApiFailure({
                status: res.status,
                code: typeof errBody.code === 'string' ? errBody.code : undefined,
                error: typeof errBody.error === 'string' ? errBody.error : undefined,
                detail: typeof errBody.detail === 'string' ? errBody.detail : undefined,
                statusText: res.statusText,
            });
            const message = failure.message;
            // 只有「真的 session 過期」才叫使用者重新登入；缺操作金鑰改發另一條通知（把設定框叫出來），
            // 否則剛登入的人會一直被叫去重新登入，而問題根本不在登入。
            if (failure.sessionExpired)
                notifySessionExpired(message);
            else if (failure.kind === 'api_token')
                notifyApiTokenRequired(message);
            // 掛上 status／code：呼叫端要靠 404 判斷「session 已死」（見 api/errors.ts）。
            const error = new Error(message);
            error.status = res.status;
            if (typeof errBody.code === 'string')
                error.code = errBody.code;
            throw error;
        }
        return body;
    }
    /**
     * `/api/admin/*`。已登入的 admin／engineer／owner 靠 session，不必先有 token。
     * sessionStorage 有 `X-Admin-Token` 才附上（機器／可選路徑）。
     *
     * 沒 token 仍打後端：前端不先丟 `admin_token_missing`。
     * 只有 401 `admin_unauthorized` 通知 UI。503 `admin_token_unset` 是後端沒設 secret
     * 且這次 session 不是 SessionStaff，不要叫人重貼 token。
     * `cache: 'no-store'` 是第二道保險（後端自己也帶 `cache-control: no-store`）。
     */
    async function adminRequest(path) {
        const token = readAdminToken();
        const headers = { accept: 'application/json', ...authHeaders() };
        if (token !== '')
            headers['x-admin-token'] = token;
        let res;
        try {
            res = await doFetch(`${base}${path}`, {
                method: 'GET',
                headers,
                cache: 'no-store',
                credentials: 'omit',
            });
        }
        catch (err) {
            throw new Error(`連不到 Worker（GET ${path}）：${errMessage(err)}`);
        }
        const text = await res.text();
        const body = text ? safeJson(text) : undefined;
        if (!res.ok) {
            const errBody = (body ?? {});
            const code = typeof errBody.code === 'string' ? errBody.code : undefined;
            const raw = typeof errBody.error === 'string' ? errBody.error : `${res.status} ${res.statusText}`;
            const message = res.status === 503 && code === 'admin_token_unset'
                ? '後端未設定 ADMIN_TOKEN，admin 端點目前是關閉的（wrangler secret put ADMIN_TOKEN）'
                : code === 'unauthenticated'
                    ? '登入已過期或沒有帶 session，請重新登入。'
                    : res.status === 401 && code === 'admin_unauthorized'
                        ? `Admin Token 未通過驗證：${raw}`
                        : `admin 請求失敗（GET ${path}）：${raw}`;
            if (res.status === 401 && code === 'admin_unauthorized')
                notifyAdminUnauthorized(message);
            const error = new Error(message);
            error.status = res.status;
            if (code !== undefined)
                error.code = code;
            throw error;
        }
        return body;
    }
    const accounts = () => request('/api/sessions');
    const account = (id) => request(`/api/sessions/${encodeURIComponent(id)}`);
    function subscribe(handlers) {
        const sockets = new Map();
        const seen = new Set();
        const known = new Map();
        let stopped = false;
        // 同一則訊息只能餵給 UI 一次：`hello.recent` 會在每次連線／重連時重播整個緩衝，
        // 沒有這個集合就會把同一批事件當成新訊息再算一次未讀（實測 200 變 400）。
        const delivered = new Set();
        const feed = (rawAccount, event, replay) => {
            const ts = typeof event.ts === 'number' ? event.ts : Date.now();
            const kind = KIND_MAP[String(event.type)] ?? 'say';
            const to = typeof event.to === 'string' && event.to !== '' ? event.to : undefined;
            const id = `${rawAccount.id}:${ts}:${String(event.from ?? '')}:${String(event.text ?? '')}`;
            if (delivered.has(id))
                return;
            delivered.add(id);
            if (delivered.size > 4000) {
                // 只留最近一批：清空後重新累積，比無上限長大安全（舊 id 不會再被重播）。
                delivered.clear();
                delivered.add(id);
            }
            handlers.onMessage({
                id,
                accountId: rawAccount.id,
                accountName: rawAccount.nickname,
                service: rawAccount.service,
                kind,
                from: String(event.from ?? ''),
                text: String(event.text ?? ''),
                ...(to ? { to } : {}),
                ts,
                room: rawAccount.room,
                ...(replay ? { replay: true } : {}),
            });
        };
        const handleEnvelope = (id, raw) => {
            let envelope;
            try {
                envelope = JSON.parse(raw);
            }
            catch {
                return;
            }
            const owner = known.get(id);
            const state = envelope.state;
            if (state && typeof state === 'object') {
                const next = toAccount({ ...state, id });
                known.set(id, next);
                handlers.onAccount(next);
                const recent = envelope.recent;
                if (Array.isArray(recent))
                    for (const ev of recent)
                        feed(next, ev, true);
            }
            const event = envelope.event;
            if (event && typeof event === 'object' && owner)
                feed(owner, event, false);
            if (envelope.type === 'error' && typeof envelope.message === 'string')
                handlers.onError?.(envelope.message);
        };
        const openSocket = (acc) => {
            if (stopped || !realtimeEnabled || sockets.has(acc.id))
                return;
            if (typeof WebSocket === 'undefined')
                return;
            const wsBase = base.replace(/^http/, 'ws');
            // WebSocket 不能帶自訂標頭（瀏覽器限制），所以憑證改用查詢參數轉送；
            // Worker 對 `/ws` 只接受 `?token=`（PIN Bearer）或 `?vm_session=`，其餘照樣 401。
            const headers = authHeaders();
            const bearer = /^Bearer\s+(.+)$/i.exec(headers['authorization'] ?? '');
            const token = bearer?.[1]?.trim() ?? '';
            const session = headers['x-vm-session'] ?? '';
            const query = token !== ''
                ? `?token=${encodeURIComponent(token)}`
                : session === ''
                    ? ''
                    : `?vm_session=${encodeURIComponent(session)}`;
            const ws = new WebSocket(`${wsBase}/api/sessions/${encodeURIComponent(acc.id)}/ws${query}`);
            sockets.set(acc.id, ws);
            ws.addEventListener('message', (ev) => {
                if (typeof ev.data === 'string')
                    handleEnvelope(acc.id, ev.data);
            });
            ws.addEventListener('error', () => handlers.onError?.(`WebSocket 錯誤：${acc.nickname}`));
            ws.addEventListener('close', () => {
                sockets.delete(acc.id);
                // 帳號還在線就重連一次（指數退避上限 15 秒）。
                if (!stopped)
                    setTimeout(() => openSocket(known.get(acc.id) ?? acc), 3000);
            });
        };
        const reconcile = async () => {
            try {
                const rows = await accounts();
                if (stopped)
                    return;
                for (const row of rows) {
                    const acc = toAccount(row);
                    known.set(acc.id, acc);
                    if (!seen.has(acc.id)) {
                        seen.add(acc.id);
                        handlers.onAccount(acc);
                    }
                    openSocket(acc);
                }
            }
            catch (err) {
                handlers.onError?.(errMessage(err));
            }
        };
        void reconcile();
        const timer = setInterval(() => void reconcile(), reconcileMs);
        return () => {
            stopped = true;
            clearInterval(timer);
            for (const ws of sockets.values()) {
                try {
                    ws.close();
                }
                catch {
                    /* 已斷 */
                }
            }
            sockets.clear();
        };
    }
    return {
        async listAccounts() {
            return (await accounts()).map(toAccount);
        },
        /** 同 `listAccounts`，但保留對話層欄位（unread / lastActivityAt / lastMessagePreview）。 */
        async listConversations() {
            return (await accounts()).map(toConversation);
        },
        /**
         * `GET /api/sessions` 的原始列：歸因欄位（`loginMode`／`transport`／`account`／`loginReused`）
         * 只在原始 JSON 裡，`toConversation()` 的白名單投影會濾掉（問題單 #13）。
         * 一樣走 `request()`，所以 session 標頭與 401 處理跟其他請求完全相同。
         */
        async listSessionRows() {
            return accounts();
        },
        async createAccount(input) {
            const row = await request('/api/sessions', {
                method: 'POST',
                body: JSON.stringify(input),
            });
            return toAccount(row);
        },
        /**
         * `GET /api/rooms?service=`。後端還沒這個端點時**要讓呼叫端知道**（面板顯示錯誤＋重試），
         * 不要靜默吞掉變成「沒有房間資料」。
         */
        async listRooms(service) {
            let body;
            try {
                body = await request(`/api/rooms?service=${encodeURIComponent(service)}`);
            }
            catch (err) {
                throw new Error(`房間列表讀取失敗（GET /api/rooms?service=${service}）：${errMessage(err)}`);
            }
            const rows = Array.isArray(body)
                ? body
                : body && typeof body === 'object' && Array.isArray(body.rooms)
                    ? body.rooms
                    : null;
            if (!rows)
                throw new Error('房間端點回傳格式看不懂（預期陣列或 { rooms: [] }）');
            return rows.map((row) => toRoomInfo(row, service));
        },
        async joinRoom(accountId, room) {
            await request(`/api/sessions/${encodeURIComponent(accountId)}/join`, {
                method: 'POST',
                body: JSON.stringify({ room }),
            });
        },
        async say(accountId, text) {
            await request(`/api/sessions/${encodeURIComponent(accountId)}/say`, {
                method: 'POST',
                body: JSON.stringify({ text }),
            });
        },
        async pm(accountId, target, text) {
            await request(`/api/sessions/${encodeURIComponent(accountId)}/pm`, {
                method: 'POST',
                body: JSON.stringify({ target, text }),
            });
        },
        async close(accountId) {
            await request(`/api/sessions/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
        },
        /**
         * `GET /api/sessions/:id/events?limit=&since=` —— 對話歷史。
         * 回應是 `{ events: [...], order: 'asc', total, lastActivityAt, unread }`，
         * 也容許直接是陣列（舊版）；兩種都收斂成 `SessionEventsPage`。
         */
        async listEvents(accountId, options = {}) {
            const params = new URLSearchParams();
            if (typeof options.limit === 'number')
                params.set('limit', String(options.limit));
            if (typeof options.since === 'number')
                params.set('since', String(options.since));
            const qs = params.toString();
            const body = await request(`/api/sessions/${encodeURIComponent(accountId)}/events${qs ? `?${qs}` : ''}`);
            const row = (body ?? {});
            const list = Array.isArray(body) ? body : Array.isArray(row.events) ? row.events : null;
            if (!list)
                throw new Error('events 端點回傳格式看不懂（預期陣列或 { events: [] }）');
            return {
                events: list.map((item) => toSessionEvent(item)),
                ...(typeof row.order === 'string' ? { order: row.order } : {}),
                ...(typeof row.total === 'number' ? { total: row.total } : {}),
                ...(typeof row.lastActivityAt === 'number' ? { lastActivityAt: row.lastActivityAt } : {}),
                ...(typeof row.unread === 'number' ? { unread: row.unread } : {}),
            };
        },
        /** `POST /api/sessions/:id/read` —— 標記已讀，回新的 `unread`。 */
        async markRead(accountId, ts) {
            const body = await request(`/api/sessions/${encodeURIComponent(accountId)}/read`, {
                method: 'POST',
                body: JSON.stringify(typeof ts === 'number' ? { ts } : {}),
            });
            const lastReadTs = typeof body.lastReadTs === 'number' ? body.lastReadTs : Date.now();
            return {
                ok: body.ok === true,
                lastReadTs,
                ...(typeof body.lastReadAt === 'number' ? { lastReadAt: body.lastReadAt } : {}),
                unread: typeof body.unread === 'number' ? body.unread : 0,
            };
        },
        /**
         * 列表（`GET /api/accounts`）。回應
         * `{accounts:[{service,username,createdAt,lastLoginAt,ok,hasToken,tokenSavedAt,rebuilds,
         * relogins,lastUsedAt,usageCount,source,held}],count}`；也容許直接是陣列（舊版）。
         * **不含密碼、也不含 token 內容**。
         *
         * 收斂一律走 `toPoolAccount()`：§4.2 的擴充欄位（`hasToken`／`rebuilds`／`source`／
         * `held`…）必須逐欄帶過去——以前只留 5 個核心欄位，畫面「憑證／重建／狀態／來源／
         * 目前佔用」就永遠 `—`。
         */
        async listAccountPool(service) {
            const qs = service ? `?service=${encodeURIComponent(service)}` : '';
            const body = await request(`/api/accounts${qs}`);
            const row = (body ?? {});
            const list = Array.isArray(body) ? body : Array.isArray(row.accounts) ? row.accounts : null;
            if (!list)
                throw new Error('帳號池端點回傳格式看不懂（預期陣列或 { accounts: [] }）');
            const accounts = list.map((item) => toPoolAccount((item ?? {})));
            return {
                accounts,
                count: typeof row.count === 'number' ? row.count : accounts.length,
            };
        },
        /**
         * `POST /api/accounts/register {service,count,room?}`：經 relay 從台灣出口註冊。
         * 一顆失敗不會讓整批翻掉；被節流時回 429，訊息裡有「請 N 秒後再試」（UI 另外自己倒數 20 秒）。
         */
        async registerAccountPool(input) {
            const body = await request('/api/accounts/register', {
                method: 'POST',
                body: JSON.stringify(input),
            });
            const rawResults = Array.isArray(body.results) ? body.results : [];
            const results = rawResults.map((item) => {
                const row = (item ?? {});
                return {
                    username: String(row.username ?? ''),
                    ok: row.ok === true,
                    ...(typeof row.reason === 'string' && row.reason !== '' ? { reason: row.reason } : {}),
                };
            });
            return {
                service: normalizeService(body.service, input.service),
                requested: typeof body.requested === 'number' ? body.requested : input.count,
                created: typeof body.created === 'number' ? body.created : results.filter((r) => r.ok).length,
                results,
                ...(typeof body.room === 'string' ? { room: body.room } : {}),
                ...(typeof body.hitSite === 'boolean' ? { hitSite: body.hitSite } : {}),
            };
        },
        /** `DELETE /api/accounts/:username?service=`：只移除池內憑證，不動已在跑的 session。 */
        /**
         * `PATCH /api/accounts/:username?service=`：改顯示名稱。
         * 後端回 400 帶具體訊息（重名／長度／空白），直接往上拋給畫面顯示。
         */
        async renameAccountPool(service, username, displayName) {
            const name = username.trim();
            if (name === '')
                throw new Error('缺少 username');
            const body = await request(`/api/accounts/${encodeURIComponent(name)}?service=${encodeURIComponent(service)}`, { method: 'PATCH', body: JSON.stringify({ displayName }) });
            const raw = (body.account ?? {});
            return toPoolAccount(raw);
        },
        async deleteAccountPool(service, username) {
            const name = username.trim();
            if (name === '')
                throw new Error('缺少 username');
            await request(`/api/accounts/${encodeURIComponent(name)}?service=${encodeURIComponent(service)}`, { method: 'DELETE' });
        },
        /* ── Admin（`GET /api/admin/*`，§4.3／§5.3）─────────────────────────
         * 一律走 `adminRequest`（session；有 token 才加 `x-admin-token`）。回傳值都先過一層正規化：
         * 後端是 no-store、欄位是白名單投影，但 `null`（沒有紀錄）與缺欄位還是會出現，
         * 直接丟給畫面會在 `.relay.configured` 這種地方炸掉。
         */
        async overview() {
            return toAdminOverview(await adminRequest('/api/admin/overview'));
        },
        /**
         * `GET /api/platforms`：後端還沒這支端點時回 `null`（不拋），前端沿用本地目錄。
         * 只認 `{platforms:[...]}`（或直接陣列）；格式不對一律當「沒有」。
         */
        async listPlatforms() {
            try {
                const body = await request('/api/platforms');
                const rows = Array.isArray(body) ? body : body.platforms;
                return Array.isArray(rows) ? rows : null;
            }
            catch {
                return null;
            }
        },
        async listRuns(query = {}) {
            const params = new URLSearchParams();
            if (typeof query.service === 'string' && query.service !== '' && query.service !== 'all') {
                params.set('service', query.service);
            }
            if (typeof query.limit === 'number' && Number.isFinite(query.limit)) {
                params.set('limit', String(Math.trunc(query.limit)));
            }
            if (typeof query.cursor === 'string' && query.cursor !== '')
                params.set('cursor', query.cursor);
            const qs = params.toString();
            return toAdminRunsPage(await adminRequest(`/api/admin/runs${qs ? `?${qs}` : ''}`));
        },
        /** `refresh=true` → `?refresh=1`（人工按「重新檢查」才強制刷新 relay／兩站，§4.3-3）。 */
        async system(refresh = false) {
            return toAdminSystemStatus(await adminRequest(`/api/admin/system${refresh ? '?refresh=1' : ''}`));
        },
        subscribe,
    };
}
/* ── Admin 回應的正規化（形狀＝types.ts 既有的 Admin*，不新增型別）─────────────
 *
 * 為什麼要這一層：後端 `admin.ts` 是白名單投影，**拿不到真值時回 `null`**（§4.3-3），
 * 但 `types.ts` 的 `AdminSystemStatus.relay`／`scheduler` 宣告成非 null。
 * 少了這層，`system.relay.configured` 會在沒設 relay 的環境直接 TypeError 白畫面。
 * 一律「缺欄位 → 中性值」，不要編造數字（`—` 由畫面依 `null` 決定）。
 */
function asRecord(value) {
    return value !== null && typeof value === 'object' ? value : {};
}
function asNum(value, fallback = 0) {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function asNumOrNull(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function asText(value, fallback = '') {
    return typeof value === 'string' ? value : fallback;
}
function asBool(value, fallback = false) {
    return typeof value === 'boolean' ? value : fallback;
}
function asService(value) {
    return normalizeService(value);
}
/**
 * 服務計數：**缺欄位 → `null`**（「沒量到」），不是 0。
 * 用 `asNum` 會把「這批沒量池子」變成「池子 0 顆」，畫面就謊報（§3 共同規則）。
 */
function asServiceCounts(value) {
    const row = asRecord(value);
    return { ek21: asNumOrNull(row.ek21), ut: asNumOrNull(row.ut) };
}
function asAlertLevel(value) {
    return value === 'info' || value === 'warn' || value === 'danger' ? value : 'info';
}
function asRunSource(value) {
    return value === 'grow_pool' || value === 'console' || value === 'manual' ? value : 'manual';
}
function asSchedulerResult(value) {
    return value === 'ok' || value === 'failed' ? value : 'unknown';
}
function toAdminOverview(raw) {
    const accounts = asRecord(raw.accounts);
    const sessions = asRecord(raw.sessions);
    const registration = asRecord(raw.registration);
    const today = asRecord(registration.today);
    const byRoom = Array.isArray(sessions.byRoom)
        ? sessions.byRoom.map((item) => {
            const row = asRecord(item);
            return {
                service: asService(row.service),
                room: asText(row.room),
                mine: asNum(row.mine),
                // 後端沒有房內人數時是 null；介面型別是 number，這裡收斂成 0（mine 本身仍是真值）。
                roomOnline: asNum(row.roomOnline),
            };
        })
        : [];
    // 沒跑的日子必須保持 null（畫面顯示 `—`）；`asNum` 會把 null 變成 0，這裡不能用它。
    const last7d = Array.isArray(registration.last7d)
        ? registration.last7d.map((item) => {
            const row = asRecord(item);
            return {
                date: asText(row.date),
                ek21: asNumOrNull(row.ek21),
                ut: asNumOrNull(row.ut),
                failed: asNumOrNull(row.failed),
            };
        })
        : [];
    const alerts = Array.isArray(raw.alerts)
        ? raw.alerts.slice(0, 5).map((item) => {
            const row = asRecord(item);
            return {
                level: asAlertLevel(row.level),
                code: asText(row.code),
                message: asText(row.message),
                at: asNum(row.at),
            };
        })
        : [];
    return {
        generatedAt: asNum(raw.generatedAt, Date.now()),
        accounts: {
            total: asNum(accounts.total),
            byService: asServiceCounts(accounts.byService),
            usable: asNum(accounts.usable),
            rejected: asNum(accounts.rejected),
            neverUsed: asNum(accounts.neverUsed),
        },
        sessions: {
            total: asNum(sessions.total),
            online: asNum(sessions.online),
            dormant: asNum(sessions.dormant),
            unreadTotal: asNum(sessions.unreadTotal),
            byRoom,
        },
        registration: {
            lastBatchAt: asNumOrNull(registration.lastBatchAt),
            throttleRemainingMs: asNum(registration.throttleRemainingMs),
            // 後端另外給 hasRun（分辨「今天沒跑」與「跑了 0 顆」）；沒給就留 undefined，畫面自己用 `—`。
            today: {
                ek21: asNumOrNull(today.ek21),
                ut: asNumOrNull(today.ut),
                failed: asNumOrNull(today.failed),
                ...(typeof today.hasRun === 'boolean' ? { hasRun: today.hasRun } : {}),
            },
            last7d,
        },
        alerts,
    };
}
function toAdminRun(value) {
    const row = asRecord(value);
    return {
        id: asText(row.id),
        at: asNum(row.at),
        source: asRunSource(row.source),
        service: asService(row.service),
        requested: asNum(row.requested),
        created: asNum(row.created),
        failed: asNum(row.failed),
        durationMs: asNum(row.durationMs),
        // 缺欄位視為「有打站方」（true）；只有明確 false 才代表全被本地檢查擋掉。
        hitSite: asBool(row.hitSite, true),
        errors: Array.isArray(row.errors)
            ? row.errors.filter((item) => typeof item === 'string')
            : [],
        // 後端可能回 null（那批沒量池子大小）；型別非 null，收斂成 0（= 沒有紀錄）。
        poolAfter: asServiceCounts(row.poolAfter),
    };
}
function toAdminRunsPage(raw) {
    return {
        runs: Array.isArray(raw.runs) ? raw.runs.map(toAdminRun) : [],
        nextCursor: typeof raw.nextCursor === 'string' && raw.nextCursor !== '' ? raw.nextCursor : null,
    };
}
function toAdminSystemStatus(raw) {
    const registration = asRecord(raw.registration);
    const relay = asRecord(raw.relay);
    const scheduler = asRecord(raw.scheduler);
    const sites = Array.isArray(raw.sites)
        ? raw.sites.map((item) => {
            const row = asRecord(item);
            return {
                service: asService(row.service),
                reachable: asBool(row.reachable),
                latencyMs: asNumOrNull(row.latencyMs),
                checkedAt: asNum(row.checkedAt),
            };
        })
        : [];
    const source = scheduler.source;
    return {
        checkedAt: asNum(raw.checkedAt, Date.now()),
        registration: {
            lastBatchAt: asNumOrNull(registration.lastBatchAt),
            throttleRemainingMs: asNum(registration.throttleRemainingMs),
        },
        // relay 整個是 null ＝ 後端沒設定（或這輪沒打），不是「掛掉」：configured:false。
        relay: {
            configured: asBool(relay.configured),
            reachable: asBool(relay.reachable),
            latencyMs: asNumOrNull(relay.latencyMs),
            forbiddenBackoffSec: asNum(relay.forbiddenBackoffSec),
            checkedAt: asNumOrNull(relay.checkedAt),
        },
        sites,
        // scheduler 是 null ＝ 還沒有 grow_pool 的批次紀錄；source `none` 給畫面顯示 `—`。
        scheduler: {
            lastRunAt: asNumOrNull(scheduler.lastRunAt),
            lastResult: asSchedulerResult(scheduler.lastResult),
            source: source === 'cron' || source === 'manual' ? source : 'none',
        },
    };
}
/** events 陣列元素 → SessionEvent；欄位壞掉就補空字串，不要讓整個對話串消失。 */
function toSessionEvent(raw) {
    const row = (raw ?? {});
    const to = typeof row.to === 'string' && row.to !== '' ? row.to : undefined;
    return {
        type: String(row.type ?? 'say'),
        from: String(row.from ?? ''),
        text: String(row.text ?? ''),
        ...(to ? { to } : {}),
        ts: typeof row.ts === 'number' ? row.ts : Date.now(),
        ...(typeof row.room === 'string' ? { room: row.room } : {}),
    };
}
function safeJson(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
function errMessage(err) {
    return err instanceof Error ? err.message : String(err);
}
//# sourceMappingURL=http-client.js.map